import type { InternalAxiosRequestConfig } from 'axios'

// Gives every API call a W3C `traceparent` header so groundcover can link a page view in the
// browser to the backend calls it made. See pageTraceContext.spec.md.
// This file imports nothing from the app on purpose: ApiClient imports it, so any app import here
// risks a circular import. The dev-mode check (PTC-FR-005) lives in ApiClient for that reason.

export const TRACEPARENT_HEADER = 'traceparent'

// A page trace is restarted after this long, so traces from polling pages stay a readable size.
export const PAGE_TRACE_MAX_AGE_MS = 60 * 1000

interface PageTrace {
  traceId: string
  path: string
  startedAt: number
}

let currentTrace: PageTrace | null = null

// Random lowercase hex id. W3C says an all-zero id is invalid, so try again if we get one.
const randomHexId = (byteLength: number): string => {
  const bytes = new Uint8Array(byteLength)
  let id = ''
  do {
    crypto.getRandomValues(bytes)
    id = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  } while (/^0+$/.test(id))
  return id
}

// PTC-FR-002: only same-origin URLs get the header. Other regions live on other hostnames and
// their CORS rules do not allow `traceparent`, so adding it there would break the call.
export const isSameOriginUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

// PTC-FR-003: keep one trace id while the user stays on the same page path, and start a new
// one when the path changes or the current trace is too old.
const getPageTraceId = (): string => {
  const path = window.location.pathname
  const now = Date.now()
  const isStale =
    !currentTrace ||
    currentTrace.path !== path ||
    now - currentTrace.startedAt > PAGE_TRACE_MAX_AGE_MS
  if (isStale) {
    currentTrace = { traceId: randomHexId(16), path, startedAt: now }
  }
  return currentTrace.traceId
}

// PTC-FR-001: build the header value `00-<trace id>-<span id>-01` for a URL, or return null when
// the URL should not get one (PTC-FR-002).
export const getTraceparentHeader = (url: string | undefined): string | null => {
  if (!url || !isSameOriginUrl(url)) return null
  return `00-${getPageTraceId()}-${randomHexId(8)}-01`
}

// Axios request interceptor. It never throws, so a tracing problem can not block a request.
export const addTraceparentHeader = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  try {
    // PTC-FR-004: never set the header twice. XHR joins repeated headers with a comma, which
    // would break the value.
    if (config.headers?.has(TRACEPARENT_HEADER)) return config
    const value = getTraceparentHeader(config.url)
    if (value) config.headers?.set(TRACEPARENT_HEADER, value)
  } catch {
    // Send the request without the header rather than failing it.
  }
  return config
}

// Test helper: forget the current page trace.
export const resetPageTrace = (): void => {
  currentTrace = null
}
